import React, { useEffect, useState, useMemo } from "react"
import * as url from "../../helpers/url_helper"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
  getMerchantGroupCommunications,
  manageMerchantGroupCommunication,
  getCommChannels,
} from "store/actions"
import { clearMerchantGroupCommunications } from "store/merchant-group/actions"
import TextInput from "components/Common/TextInput"
import SelectInput from "components/Common/SelectInput"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function MerchantGroupCommunicationForm({
  page,
  match,
  history,
  onTabCompletion,
  activeTab,
  tabId,
  onFormRef,
  onReadOnlyChange,
  navigateToNextTab,
  createdMerchantGroupID,
}) {
  const [readOnly, setReadOnly] = useState(page === "detail")
  const [showAddForm, setShowAddForm] = useState(page === "add")
  const [editingCommunication, setEditingCommunication] = useState(null)
  const [editingCommId, setEditingCommId] = useState(null)
  const [editValue, setEditValue] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasData, setHasData] = useState(false)
  const [previousCommunicationsLength, setPreviousCommunicationsLength] =
    useState(0)

  const dispatch = useDispatch()
  const detailID = match?.params?.id

  // Use createdMerchantGroupID in add mode, detailID in detail mode
  const merchantGroupID = page === "add" ? createdMerchantGroupID : detailID

  const { data, communicationError, isLoadingCommunications } = useSelector(
    state => ({
      data: state.merchantGroup,
      communicationError: state.merchantGroup.communicationError,
      isLoadingCommunications:
        state.merchantGroup.isLoadingCommunications || false,
    })
  )

  const merchantGroup = data?.merchantGroups?.find(
    item => item.merchantGroupID === detailID
  )

  const communications = data?.merchantGroupCommunications || []
  const { commChannels } = useSelector(state => ({
    commChannels: state.appInfo.commChannels || [],
  }))

  // Function to check if error is related to "no data found"
  const isNoDataError = error => {
    if (!error) return false

    const errorMessage = error.message || error.description || ""
    const errorCode = error.code || error.status || ""


    // Check for common "no data found" patterns
    const noDataPatterns = ["no content available"]

    return !noDataPatterns.some(
      pattern =>
        errorMessage.toLowerCase().includes(pattern) ||
        errorCode.toString().includes("404")
    )
  }

  // Fetch communication channels on component mount
  useEffect(() => {
    if (!commChannels.length) {
      dispatch(
        getCommChannels({
          headers: {
            "input-parameters": {},
          },
        })
      )
    }
  }, [dispatch, commChannels.length])

  // Fetch communications only when tab is active
  useEffect(() => {
    const isTabActive = activeTab === tabId

    if (merchantGroupID && isTabActive) {
      dispatch(
        getMerchantGroupCommunications({
          headers: {
            "input-parameters": { merchantGroupID: merchantGroupID },
          },
        })
      )
    }
  }, [dispatch, merchantGroupID, activeTab, tabId])

  // Monitor communications and update hasData state
  useEffect(() => {
    setHasData(communications && communications.length > 0)
  }, [communications])

  // Track communications length changes
  useEffect(() => {
    setPreviousCommunicationsLength(communications.length)
  }, [communications.length])

  // Expose form methods to parent component and update readonly state
  useEffect(() => {
    if (onFormRef && page === "add") {
      onFormRef({
        handleSubmit: () => {
          // For communication form, we just mark as completed since it's optional
          if (navigateToNextTab) {
            navigateToNextTab()
          }
        },
        submitForm: () =>
          new Promise(resolve => {
            resolve()
            if (navigateToNextTab) {
              navigateToNextTab()
            }
          }),
      })
    }
  }, [onFormRef, page, navigateToNextTab])

  // Update readonly state based on data
  useEffect(() => {
    if (page === "add" && hasData && !readOnly) {
      setReadOnly(true)
      if (onReadOnlyChange) {
        onReadOnlyChange(true)
      }
    }
  }, [hasData, page, readOnly, onReadOnlyChange])

  // Convert comm channels to options for select
  const channelOptions = useMemo(() => {
    return commChannels.map(channel => ({
      value: channel.commChannelID,
      label: channel.name,
      meaning: channel.meaning,
      image: channel.image,
    }))
  }, [commChannels])

  // Function to handle communication submission after confirmation
  const handleCommunicationSubmit = (values) => {
    setIsSubmitting(true)
    const payload = {
      merchantGroupID: merchantGroupID || merchantGroup?.merchantGroupID,
      commChannelID: values.commChannelID,
      channelValue: values.channelValue,
      actionType: editingCommunication ? "update" : "create",
    }

    if (editingCommunication) {
      payload.merchantGroupCommunicationID =
        editingCommunication.merchantGroupCommunicationID
    }

    dispatch(manageMerchantGroupCommunication(payload, {}, history))

    // Don't set isSubmitting to false here - let the useEffect handle it based on success/error
  }

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      commChannelID: editingCommunication?.commChannel?.commChannelID || "",
      channelValue: editingCommunication?.channelValue || "",
    },
    validationSchema: Yup.object({
      commChannelID: Yup.string().required(
        "Please select a communication channel"
      ),
      channelValue: Yup.string()
        .required("Please enter the channel value")
        .test("channel-format", "Invalid format", function (value) {
          const { commChannelID } = this.parent
          if (!commChannelID || !value) return true

          const channel = commChannels.find(
            ch => ch.commChannelID === commChannelID
          )

          if (!channel) return true

          // Validate based on channel meaning or name
          const channelName = channel.meaning || channel.name || ""

          if (channelName.toLowerCase().includes("email")) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          }

          if (
            channelName.toLowerCase().includes("phone") ||
            channelName.toLowerCase().includes("mobile") ||
            channelName.toLowerCase().includes("whatsapp")
          ) {
            return /^[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(
              value
            )
          }

          if (
            channelName.toLowerCase().includes("website") ||
            channelName.toLowerCase().includes("url")
          ) {
            return /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(
              value
            )
          }

          return true
        })
        .max(255, "Channel Value cannot be greater than 255"),
    }),
    onSubmit: async (values) => {
      const isValid = await validateBeforeConfirm(validation)
      if (isValid) {
        handleCommunicationSubmit(values)
      }
    },
  })

  // Monitor for successful communication submission and reset form
  useEffect(() => {
    // Check if a new communication was added (length increased)
    if (
      communications.length > previousCommunicationsLength &&
      !communicationError
    ) {
      // Reset form on successful submission
      setTimeout(() => {
        setEditingCommunication(null)
        validation.resetForm()
        setIsSubmitting(false)
        // Update the previous length to current length
        setPreviousCommunicationsLength(communications.length)
      }, 500)
    } else if (communicationError && isSubmitting) {
      // Handle error case
      setIsSubmitting(false)
    }
  }, [
    communicationError,
    communications.length,
    previousCommunicationsLength,
    isSubmitting,
    validation,
  ])

  const handleAddNew = () => {
    setEditingCommunication(null)
    validation.resetForm()
    setShowAddForm(true)
  }

  const handleEdit = communication => {
    setEditingCommId(communication.merchantGroupCommunicationID)
    setEditValue(communication.channelValue)
  }

  const handleSaveEdit = communication => {
    const channel = communication.commChannel
    const channelName = channel.meaning || channel.name || ""

    // Validate based on channel type
    let isValid = true
    let errorMsg = ""

    if (channelName.toLowerCase().includes("email")) {
      isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editValue)
      errorMsg = "Please enter a valid email address"
    } else if (
      channelName.toLowerCase().includes("phone") ||
      channelName.toLowerCase().includes("mobile") ||
      channelName.toLowerCase().includes("whatsapp")
    ) {
      isValid =
        /^[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(
          editValue
        )
      errorMsg = "Please enter a valid phone number"
    } else if (
      channelName.toLowerCase().includes("website") ||
      channelName.toLowerCase().includes("url")
    ) {
      isValid =
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(
          editValue
        )
      errorMsg = "Please enter a valid URL"
    }

    if (!editValue.trim()) {
      return
    }

    if (!isValid) {
      return
    }

    const payload = {
      merchantGroupID: merchantGroupID,
      merchantGroupCommunicationID: communication.merchantGroupCommunicationID,
      commChannelID: communication.commChannel.commChannelID,
      channelValue: editValue,
      actionType: "update",
    }

    dispatch(manageMerchantGroupCommunication(payload, {}, history))
    setEditingCommId(null)
    setEditValue("")
  }

  const handleCancelEdit = () => {
    setEditingCommId(null)
    setEditValue("")
  }

  const handleDelete = communication => {
    setIsSubmitting(true)
    const payload = {
      merchantGroupID: merchantGroupID,
      merchantGroupCommunicationID: communication.merchantGroupCommunicationID,
      actionType: "delete",
    }
    dispatch(manageMerchantGroupCommunication(payload, {}, history))

    setTimeout(() => {
      setIsSubmitting(false)
    }, 500)
  }

  const handleCancel = () => {
    // Hide form in detail mode when cancel is clicked
    if (page === "detail") {
      setShowAddForm(false)
    }
    setEditingCommunication(null)
    validation.resetForm()
  }

  const getChannelIcon = channel => {
    // If channel has an image URL, use it
    if (channel.image) {
      return (
        <img
          src={channel.image}
          alt={channel.name}
          style={{ width: "24px", height: "24px", filter: "invert(1)" }}
        />
      )
    }

    // Otherwise, use default icons based on channel name/meaning
    const channelName = (channel.meaning || channel.name || "").toLowerCase()

    if (channelName.includes("email"))
      return <i className="bx bx-envelope fs-4"></i>
    if (channelName.includes("phone") || channelName.includes("mobile"))
      return <i className="bx bx-phone fs-4"></i>
    if (channelName.includes("whatsapp"))
      return <i className="bx bxl-whatsapp fs-4"></i>
    if (channelName.includes("facebook"))
      return <i className="bx bxl-facebook fs-4"></i>
    if (channelName.includes("twitter"))
      return <i className="bx bxl-twitter fs-4"></i>
    if (channelName.includes("instagram"))
      return <i className="bx bxl-instagram fs-4"></i>
    if (channelName.includes("linkedin"))
      return <i className="bx bxl-linkedin fs-4"></i>
    if (channelName.includes("website") || channelName.includes("url"))
      return <i className="bx bx-globe fs-4"></i>

    return <i className="bx bx-info-circle fs-4"></i>
  }

  const getPlaceholder = () => {
    const channelId = validation.values.commChannelID
    if (!channelId) return "Enter channel value"

    const channel = commChannels.find(ch => ch.commChannelID === channelId)

    if (!channel) return "Enter channel value"

    const channelName = (channel.meaning || channel.name || "").toLowerCase()

    if (channelName.includes("email")) return "Enter email address"
    if (channelName.includes("phone") || channelName.includes("mobile"))
      return "Enter phone number"
    if (channelName.includes("whatsapp")) return "Enter WhatsApp number"
    if (channelName.includes("website") || channelName.includes("url"))
      return "Enter website URL"
    if (channelName.includes("facebook"))
      return "Enter Facebook profile/page URL"
    if (channelName.includes("twitter")) return "Enter Twitter handle"
    if (channelName.includes("instagram")) return "Enter Instagram handle"
    if (channelName.includes("linkedin")) return "Enter LinkedIn profile URL"

    return "Enter channel value"
  }


  return (
    <>
      {communicationError && !isNoDataError(communicationError) && (
        <Alert color="danger" className="mb-3">
          {communicationError.message || "An error occurred"}
        </Alert>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="mb-4">
          <CardBody>
            <h6 className="mb-3">
              {editingCommunication
                ? "Edit Communication Channel"
                : "Add New Communication Channel"}
            </h6>
            <Form
              onSubmit={e => {
                e.preventDefault()
                validation.handleSubmit()
                return false
              }}
            >
              <Row>
                <Col md="6">
                  <SelectInput
                    name="commChannelID"
                    label="Communication Channel"
                    placeholder="Select Channel..."
                    options={channelOptions}
                    validation={validation}
                    required={true}
                    onSelectionChange={(selectedOption) => {
                      // Set the commChannelID as string and clear channel value when channel changes
                      validation.setFieldValue("commChannelID", selectedOption ? selectedOption.value : "")
                      validation.setFieldValue("channelValue", "")
                    }}
                  />
                </Col>
                <Col md="6">
                  <TextInput
                    helperText={"Max 255 chars allowed"}
                    label={"Channel Value"}
                    required={true}
                    maxLength={255}
                    name={"channelValue"}
                    type="text"
                    placeholder={getPlaceholder()}
                    validation={validation}
                  />
                </Col>
              </Row>
              <div className="text-end">
                {page === "detail" && (
                  <CButton.Secondary
                    text="Cancel"
                    className="me-2"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  />
                )}
                <ConfirmButton
                  contextUri={url.MANAGE_MERCHANT_GROUP_COMMUNICATION}
                  actionType="post"
                  validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                  onConfirm={() => handleCommunicationSubmit(validation.values)}
                  message={`Are you sure you want to ${
                    editingCommunication ? "update" : "add"
                  } this communication channel?`}
                  disabled={isSubmitting}
                  renderButton={
                    <CButton.Primary
                      text={
                        editingCommunication
                          ? "Update Channel"
                          : "Add Channel"
                      }
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    />
                  }
                />
              </div>
            </Form>
          </CardBody>
        </Card>
      )}

      {/* Communications List */}
      <SimpleTableContainer
        title="Communication Channels"
        data={communications.map(comm => ({
          ...comm,
          id: comm.merchantGroupCommunicationID,
        }))}
        columns={[
          {
            key: "channel",
            label: "Channel",
            render: item => (
              <div className="d-flex align-items-center">
                <span className="me-2">{getChannelIcon(item.commChannel)}</span>
                {item.commChannel.name}
              </div>
            ),
          },
          {
            key: "channelValue",
            label: "Value",
          },
        ]}
        loading={isLoadingCommunications}
        emptyMessage="No communication channels added yet"
        emptyIcon="bx bx-info-circle"
        enableInlineEdit={true}
        editingId={editingCommId}
        editValue={editValue}
        onEditValueChange={setEditValue}
        editableField="channelValue"
        onEdit={handleEdit}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        onDelete={handleDelete}
        customActions={item => {
          if (editingCommId === item.merchantGroupCommunicationID) {
            return (
              <>
                <ConfirmButton
                  onConfirm={() => handleSaveEdit(item)}
                  message="Are you sure you want to update this communication channel value?"
                  renderButton={<IconButton.Save className="me-2" />}
                />
                <IconButton.Cancel onClick={handleCancelEdit} />
              </>
            )
          }

          return (
            <>
              <AccessControlWrapper
                contextUri={url.MANAGE_MERCHANT_GROUP_COMMUNICATION}
                actionType="post"
              >
                <IconButton.Edit
                  className="me-2"
                  onClick={() => handleEdit(item)}
                  disabled={isSubmitting || editingCommId !== null}
                />
              </AccessControlWrapper>
              <ConfirmButton
                contextUri={url.MANAGE_MERCHANT_GROUP_COMMUNICATION}
                actionType="post"
                onConfirm={() => handleDelete(item)}
                message="Are you sure you want to delete this communication channel?"
                disabled={isSubmitting || editingCommId !== null}
                renderButton={<IconButton.Delete />}
              />
            </>
          )
        }}
        headerActions={
          !showAddForm &&
          page === "detail" && (
            <AccessControlWrapper
              contextUri={url.MANAGE_MERCHANT_GROUP_COMMUNICATION}
              actionType="post"
            >
              <CButton.Success
                icon="bx bx-plus"
                text="Add Channel"
                onClick={handleAddNew}
              />
            </AccessControlWrapper>
          )
        }
      />
    </>
  )
}
